// Hook: Live position & holding tracking with P&L computation
import { useMemo } from 'react';
import { useTrading } from '../context/TradingContext';

export function usePositionTracker() {
  const { positions, holdings, getQuote, connectionStatus } = useTrading();

  const enrichedPositions = useMemo(() => {
    if (!positions || !Array.isArray(positions)) return [];

    return positions.map(pos => {
      const tick = getQuote(pos.exchange, pos.symboltoken);
      const ltp = tick?.ltp || parseFloat(pos.ltp) || 0;
      const netQty = parseInt(pos.netqty) || 0;
      const avgPrice = parseFloat(pos.netprice) || 0;

      const currentValue = Math.abs(netQty) * ltp;
      const investedValue = Math.abs(netQty) * avgPrice;
      const unrealizedPnL = netQty > 0
        ? currentValue - investedValue
        : investedValue - currentValue;
      const realizedPnL = parseFloat(pos.realised) || 0;
      const totalPnL = unrealizedPnL + realizedPnL;
      const pnlPercent = investedValue > 0 ? (unrealizedPnL / investedValue) * 100 : 0;

      return {
        ...pos,
        ltp,
        currentValue,
        investedValue,
        unrealizedPnL,
        realizedPnL,
        totalPnL,
        pnlPercent,
        isProfit: totalPnL >= 0,
      };
    });
  }, [positions, getQuote]);

  const enrichedHoldings = useMemo(() => {
    if (!holdings || !Array.isArray(holdings)) return [];

    return holdings.map(hold => {
      const tick = getQuote(hold.exchange, hold.symboltoken);
      const ltp = tick?.ltp || parseFloat(hold.ltp) || 0;
      const avgPrice = parseFloat(hold.averageprice) || 0;
      const qty = parseInt(hold.quantity) || 0;
      const currentValue = qty * ltp;
      const investedValue = qty * avgPrice;
      const pnl = currentValue - investedValue;
      const pnlPercent = investedValue > 0 ? (pnl / investedValue) * 100 : 0;

      return {
        ...hold,
        ltp,
        currentValue,
        investedValue,
        pnl,
        pnlPercent,
        isProfit: pnl >= 0,
      };
    });
  }, [holdings, getQuote]);

  const positionSummary = useMemo(() => {
    const totalPnL = enrichedPositions.reduce((sum, p) => sum + p.totalPnL, 0);
    const totalInvested = enrichedPositions.reduce((sum, p) => sum + p.investedValue, 0);
    const openCount = enrichedPositions.filter(p => parseInt(p.netqty) !== 0).length;
    return { totalPnL, totalInvested, openCount };
  }, [enrichedPositions]);

  const holdingSummary = useMemo(() => {
    const totalPnL = enrichedHoldings.reduce((sum, h) => sum + h.pnl, 0);
    const totalInvested = enrichedHoldings.reduce((sum, h) => sum + h.investedValue, 0);
    const totalCurrent = enrichedHoldings.reduce((sum, h) => sum + h.currentValue, 0);
    return { totalPnL, totalInvested, totalCurrent, count: enrichedHoldings.length };
  }, [enrichedHoldings]);

  return {
    positions: enrichedPositions,
    holdings: enrichedHoldings,
    positionSummary,
    holdingSummary,
    isLive: connectionStatus === 'connected',
  };
}
